import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminEmailApi } from './service';
import type {
  EmailTestMode,
  ListEmailLogsParams,
  TestSmtpPayload,
  UpdateSmtpPayload,
} from './types';

export const adminEmailKeys = {
  all: ['admin-email'] as const,
  smtp: () => [...adminEmailKeys.all, 'smtp'] as const,
  testMode: () => [...adminEmailKeys.all, 'test-mode'] as const,
  appStatus: () => [...adminEmailKeys.all, 'app-status'] as const,
  emailLogList: (params: ListEmailLogsParams = {}) =>
    [...adminEmailKeys.all, 'logs', params] as const,
  emailLog: (id: string) => [...adminEmailKeys.all, 'log', id] as const,
};

export function useSmtpConfig() {
  return useQuery({
    queryKey: adminEmailKeys.smtp(),
    queryFn: () => adminEmailApi.getSmtp(),
  });
}

export function useUpdateSmtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSmtpPayload) => adminEmailApi.updateSmtp(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminEmailKeys.smtp() });
    },
  });
}

export function useTestSmtp() {
  return useMutation({
    mutationFn: (input: TestSmtpPayload) => adminEmailApi.testSmtp(input),
  });
}

export function useEmailTestMode() {
  return useQuery({
    queryKey: adminEmailKeys.testMode(),
    queryFn: () => adminEmailApi.getEmailTestMode(),
  });
}

export function useUpdateEmailTestMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmailTestMode) =>
      adminEmailApi.updateEmailTestMode(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminEmailKeys.testMode() });
      qc.invalidateQueries({ queryKey: adminEmailKeys.appStatus() });
    },
  });
}

export function useEmailLogs(params: ListEmailLogsParams = {}) {
  return useQuery({
    queryKey: adminEmailKeys.emailLogList(params),
    queryFn: () => adminEmailApi.listEmailLogs(params),
  });
}

export function useEmailLog(id: string | undefined) {
  return useQuery({
    queryKey: id ? adminEmailKeys.emailLog(id) : adminEmailKeys.all,
    queryFn: () => adminEmailApi.getEmailLog(id as string),
    enabled: Boolean(id),
  });
}

const ONE_MIN = 60 * 1000;

/**
 * Banner-only status. Refetched every minute so a superadmin who
 * just toggled test mode in another tab sees the banner appear /
 * disappear without a hard refresh.
 */
export function useAppStatus() {
  return useQuery({
    queryKey: adminEmailKeys.appStatus(),
    queryFn: () => adminEmailApi.getAppStatus(),
    refetchInterval: ONE_MIN,
  });
}
